import { ChooseUsername } from './UsernameForm.js';
import { Icon } from '../ui/icons.js';
import { useIsMobile } from '../../lib/use-mobile.js';
import { useCallback, useEffect, useState } from 'react';

import { BASE_PATH } from '@ai-studio/shared';

import { api } from '../../lib/api.js';
import { useAuth } from '../../state/auth.js';
import { useProjectTabs } from '../../state/project-tabs.js';
import { ProjectFrames, ProjectTabStrip, useTabHost } from '../ProjectTabs.js';
import { AccountPage } from './AccountPage.js';
import { DownloadPage } from './DownloadPage.js';
import { HomePage } from './HomePage.js';
import { GamePage, LibraryPage } from './LibraryPages.js';
import { LinkPage } from './LinkPage.js';
import { ProjectsPage } from './ProjectsPage.js';
import { SocialBell } from '../social/SocialBell.js';
import '../../styles-website.css';
import '../../styles-site.css';
import '../../styles-social.css';

export interface SiteUser {
  id: number;
  email: string;
  /** The username (real names are never shown). */
  name: string;
  username: string | null;
  /** A first sign-in picks a username before anything else. */
  needsUsername?: boolean;
  avatar: string | null;
  rank: { id: number; name: string; color: string } | null;
}

/** Client-side navigation inside the website (paths under BASE_PATH). */
export function navigate(path: string): void {
  // A site page in front of any open project tab.
  useProjectTabs.getState().focus(null);
  window.history.pushState(null, '', `${BASE_PATH}${path}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function signInUrl(returnTo = window.location.pathname + window.location.search): string {
  return `${BASE_PATH}/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;
}

function currentPath(): string {
  const p = window.location.pathname;
  const rest = p.startsWith(BASE_PATH) ? p.slice(BASE_PATH.length) : p;
  return rest === '' ? '/' : rest;
}

export function Link({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={`${BASE_PATH}${to}`}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/**
 * The Prism website: the public game library, Google sign-in, an account's
 * projects (each opens the browser IDE in a project tab) and its account page.
 */
export function WebsiteApp() {
  const [path, setPath] = useState(currentPath);
  const [user, setUser] = useState<SiteUser | null>(null);
  const [ready, setReady] = useState(false);
  const bootstrap = useAuth((s) => s.bootstrap);
  const activeTab = useProjectTabs((s) => s.active);
  const hasTabs = useProjectTabs((s) => s.tabs.length > 0);
  const mobile = useIsMobile();
  const [banner, setBanner] = useState<string | null>(() => new URLSearchParams(window.location.search).get('signin_error'));

  useTabHost();

  const refreshUser = useCallback(async () => {
    try {
      const session = await api.get<{ authenticated: boolean; user: SiteUser | null }>('/auth/session');
      setUser(session.authenticated ? session.user : null);
    } catch {
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
    void refreshUser();
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [bootstrap, refreshUser]);

  // A signed-out visitor has no project tabs.
  useEffect(() => {
    if (ready && !user) {
      for (const tab of useProjectTabs.getState().tabs) useProjectTabs.getState().remove(tab.projectId);
    }
  }, [ready, user]);

  async function signOut() {
    await api.post('/auth/logout').catch(() => undefined);
    for (const tab of useProjectTabs.getState().tabs) useProjectTabs.getState().remove(tab.projectId);
    setUser(null);
    navigate('/');
  }

  // Where a notification points (the bell handles friends and messages itself).
  function followLink(link: string) {
    if (link === 'invitations') return navigate('/projects');
    if (link.startsWith('game:')) return navigate(`/games/${link.slice(5)}`);
    if (link.startsWith('project:')) {
      const id = Number(link.slice(8));
      const known = useProjectTabs.getState().tabs.find((t) => t.projectId === id);
      useProjectTabs.getState().open(id, known?.name ?? 'Project');
    }
  }

  let page: React.ReactNode;
  const choosing = Boolean(user?.needsUsername);
  const game = /^\/games\/([a-z0-9-]+)\/?$/.exec(path);
  if (choosing) page = <ChooseUsername onDone={() => void refreshUser()} />;
  else if (game) page = <GamePage slug={game[1]!} user={user} />;
  else if (path.startsWith('/projects')) page = <ProjectsPage user={user} ready={ready} />;
  else if (path.startsWith('/account')) page = <AccountPage user={user} ready={ready} onChanged={refreshUser} />;
  else if (path.startsWith('/link')) page = <LinkPage user={user} ready={ready} />;
  else if (path.startsWith('/games')) page = <LibraryPage user={user} />;
  else if (path.startsWith('/download')) page = <DownloadPage />;
  else page = <HomePage user={user} />;

  return (
    <div className="site-root">
      <header className="site-nav">
        <Link to="/" className="site-brand">
          <span className="site-logo" aria-hidden="true" />
          <strong>Prism Engine Cloud</strong>
        </Link>
        <nav className="site-links">
          <Link to="/games" className={path.startsWith('/games') ? 'is-active' : ''}>
            Arcade
          </Link>
          <Link to="/download" className={path.startsWith('/download') ? 'is-active' : ''}>
            Download
          </Link>
          {user && (
            <Link to="/projects" className={path.startsWith('/projects') ? 'is-active' : ''}>
              My projects
            </Link>
          )}
          {user && (
            <Link to="/account" className={path.startsWith('/account') ? 'is-active' : ''}>
              Account
            </Link>
          )}
        </nav>
        <div className="site-user">
          {!ready ? null : user ? (
            <>
              {!choosing && <SocialBell onLink={followLink} />}
              {user.rank && (
                <span className="rank-badge" style={{ borderColor: user.rank.color, color: user.rank.color }}>
                  {user.rank.name}
                </span>
              )}
              {user.avatar ? <img className="avatar" src={user.avatar} alt="" referrerPolicy="no-referrer" /> : null}
              <span className="site-user-name">{user.username ?? ''}</span>
              <button className="ghost" type="button" onClick={() => void signOut()}>
                Sign out
              </button>
            </>
          ) : (
            <a className="btn btn-primary" href={signInUrl()}>
              Sign in
            </a>
          )}
        </div>
      </header>
      {banner && (
        <div className="site-banner" role="alert">
          {banner}
          <button className="ghost" type="button" onClick={() => setBanner(null)}>
            Dismiss
          </button>
        </div>
      )}
      {user && !choosing && hasTabs && (
        <div className="ptabs-bar">
          <ProjectTabStrip variant="bar" />
        </div>
      )}
      <main className="site-main" hidden={activeTab !== null && !choosing}>
        {page}
        <SiteFooter />
      </main>
      {user && !choosing && <ProjectFrames />}
      {mobile && activeTab === null && (
        // Phones: the site's sections at the thumb.
        <nav className="site-mtabs" aria-label="Sections">
          <Link to="/games" className={`mtab${path.startsWith('/games') || path === '/' ? ' is-on' : ''}`}>
            <Icon name="play" size={20} />
            <span>Arcade</span>
          </Link>
          <Link to="/projects" className={`mtab${path.startsWith('/projects') ? ' is-on' : ''}`}>
            <Icon name="sparkles" size={20} />
            <span>Create</span>
          </Link>
          {user ? (
            <Link to="/account" className={`mtab${path.startsWith('/account') ? ' is-on' : ''}`}>
              <Icon name="settings" size={20} />
              <span>Account</span>
            </Link>
          ) : (
            <a className="mtab" href={signInUrl()}>
              <Icon name="settings" size={20} />
              <span>Sign in</span>
            </a>
          )}
        </nav>
      )}
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="site-footer-brand">
          <span className="site-logo" aria-hidden="true" />
          <div>
            <strong>Prism Engine Cloud</strong>
            <p className="muted small">A game engine with Prisma, your AI game-dev companion, built in.</p>
          </div>
        </div>
        <nav className="site-footer-links">
          <Link to="/">Home</Link>
          <Link to="/games">Arcade</Link>
          <Link to="/download">Download</Link>
          <Link to="/projects">My projects</Link>
        </nav>
        <p className="muted small">© {new Date().getFullYear()} Prism Engine Cloud. Made for people who make games.</p>
      </div>
      <div className="spectrum-bar" aria-hidden="true" />
    </footer>
  );
}
